/**
 * Coaching Panels — Crisis + Professional Support + Usage + Consent (Compass)
 *
 * Styled to Relationship Compass's dark theme (slate, with rose accents).
 *
 * Australian English throughout.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Phone, MessageCircle, ExternalLink, Heart, ShieldCheck, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { coachCardLines, textLineSmsHref } from '../../../../../packages/crisis-support/src/textLines.js';

export function CrisisResourcesCard({ resources }) {
  if (!resources?.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-red-500/60 bg-red-950/40 p-5 shadow-lg"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="flex-shrink-0 mt-1 text-red-400" size={22} aria-hidden="true" />
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-red-200">Please speak to someone right now</h3>
          <p className="text-sm sm:text-base text-red-100 mt-1 leading-relaxed">
            What you're sharing deserves real, human support. Please reach out. They're trained for this and they're here 24/7.
          </p>
          <ul className="mt-3 space-y-2">
            {coachCardLines(resources).map((r) => (
              <li key={r.name} className="text-sm sm:text-base">
                <span className="font-semibold text-red-100">{r.name}</span>
                <span className="text-red-200">: {r.detail}</span>
                {r.phone && (
                  <a href={`tel:${r.phone.replace(/\s/g, '')}`} className="ml-2 inline-flex items-center gap-1 font-bold text-red-300 hover:text-red-100">
                    <Phone size={14} /> {r.phone}
                  </a>
                )}
                {r.text && (textLineSmsHref(r.text) ? (
                  <a href={textLineSmsHref(r.text)} className="ml-2 inline-flex items-center gap-1 font-bold text-red-300 hover:text-red-100">
                    <MessageCircle size={14} /> {r.text}
                  </a>
                ) : (
                  <span className="ml-2 inline-flex items-center gap-1 font-bold text-red-300">
                    <MessageCircle size={14} /> {r.text}
                  </span>
                ))}
                {r.url && (
                  <a href={r.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-red-300 hover:text-red-100" aria-label={`${r.name} website`}>
                    <ExternalLink size={14} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

export function ProfessionalSupportCard({ support }) {
  if (!support) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-amber-500/40 bg-amber-950/30 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <Heart className="flex-shrink-0 mt-1 text-amber-400" size={20} aria-hidden="true" />
        <div className="flex-1">
          <h4 className="font-semibold text-amber-200 text-sm sm:text-base">Consider: {support.type}</h4>
          <p className="text-sm sm:text-base text-amber-100 mt-1 leading-relaxed">{support.rationale}</p>
          {support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm sm:text-base text-amber-100 list-disc pl-5">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          {support.country && support.country !== 'INTERNATIONAL' && (
            <p className="text-xs text-amber-300/80 mt-2">Resources shown for {support.country}.</p>
          )}
          <p className="text-xs sm:text-sm text-amber-300 mt-3 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function SubtleProfessionalHint({ support }) {
  const [expanded, setExpanded] = useState(false);
  if (!support) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-sky-500/30 bg-sky-950/30 px-4 py-3">
      <div className="flex items-start gap-2">
        <Info className="flex-shrink-0 mt-0.5 text-sky-400" size={16} aria-hidden="true" />
        <div className="flex-1 text-sm sm:text-base text-sky-100 leading-relaxed">
          <p>{support.rationale}</p>
          <button onClick={() => setExpanded(!expanded)} className="mt-1.5 flex items-center gap-1 text-xs sm:text-sm font-medium text-sky-300 hover:text-sky-100" aria-expanded={expanded}>
            How to find one {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expanded && support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs sm:text-sm text-sky-100 list-disc pl-4">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          <p className="text-xs text-sky-300/80 mt-2 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function CoachingDisclaimer() {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-xs sm:text-sm text-slate-300 leading-snug">
      <Info className="flex-shrink-0 text-slate-400" size={15} aria-hidden="true" />
      <span>AI coaching is not professional advice. For deeper support, <span className="font-medium text-white">speak with a qualified professional</span>.</span>
    </div>
  );
}

export function UsageIndicator({ usage }) {
  if (!usage) return null;
  const { tier, used, dailyLimit, remaining, upgradeUrl } = usage;
  const pct = dailyLimit ? Math.min(100, (used / dailyLimit) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
      <div className="flex-1 max-w-[120px] h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full ${pct >= 80 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <span>{used}/{dailyLimit} today</span>
      {tier === 'free' && upgradeUrl && remaining <= 2 && (
        <a href={upgradeUrl} className="ml-2 font-medium text-rose-300 hover:text-rose-200 underline">Upgrade</a>
      )}
    </div>
  );
}

const COUNTRIES = [
  { code: 'AU', label: 'Australia' }, { code: 'NZ', label: 'New Zealand' },
  { code: 'IN', label: 'India' }, { code: 'UK', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' }, { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' }
];

export function ConsentGate({ onAccept, onDecline }) {
  const [firstName, setFirstName] = useState('');
  const [country, setCountry] = useState('');
  const [style, setStyle] = useState('supportive');
  const [sharing, setSharing] = useState('app-only');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onAccept({
      personalContext: { firstName: firstName.trim(), country: country || null, preferredCoachingStyle: style, communicationPreference: 'balanced', pace: 'moderate' },
      dataSharingScope: sharing
    });
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="text-rose-400" size={28} aria-hidden="true" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Before we start coaching</h1>
      </div>
      <div className="space-y-3 text-sm sm:text-base text-slate-300 leading-relaxed">
        <p>Coaching is different from Q&A. To work well, I remember your goals, struggles, and important people in your life across sessions. That means storing a small profile about you.</p>
        <p><strong>What gets stored:</strong> goals you set, names you mention, session summaries, and patterns I notice over time. You can view, edit, export, or delete all of it at any time.</p>
        <p><strong>What I'm not:</strong> a therapist, counsellor, doctor, or lawyer. When conversations get deep, I'll name that clearly and point you to real professional support.</p>
        <p className="text-slate-400 italic">What the coach remembers is stored in Australia. To write each reply, your message is sent to Anthropic&apos;s Claude, which doesn&apos;t use it to train AI. We never sell your data or share it with advertisers.</p>
      </div>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="coach-first-name" className="block text-sm font-medium text-slate-200 mb-1">What should I call you?</label>
          <input id="coach-first-name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value.slice(0, 40))} placeholder="First name or nickname" className="w-full rounded-lg border border-slate-600 bg-slate-900/60 text-slate-100 placeholder:text-slate-400 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500" maxLength={40} />
        </div>
        <div>
          <label htmlFor="coach-country" className="block text-sm font-medium text-slate-200 mb-1">Where are you based? <span className="text-slate-400 font-normal">(so we can show the right crisis and support resources)</span></label>
          <select id="coach-country" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-lg border border-slate-600 bg-slate-900 text-slate-100 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500">
            <option value="">Select a country</option>
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="coach-style" className="block text-sm font-medium text-slate-200 mb-1">Coaching style</label>
          <select id="coach-style" value={style} onChange={(e) => setStyle(e.target.value)} className="w-full rounded-lg border border-slate-600 bg-slate-900 text-slate-100 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500">
            <option value="supportive">Supportive: warm, gentle, validating</option>
            <option value="facilitative">Facilitative: asks more than tells</option>
            <option value="directive">Directive: clear suggestions and frameworks</option>
            <option value="challenging">Challenging: pushes me to see blind spots</option>
          </select>
        </div>
        <div>
          <label htmlFor="coach-sharing" className="block text-sm font-medium text-slate-200 mb-1">Data sharing across DDD apps</label>
          <select id="coach-sharing" value={sharing} onChange={(e) => setSharing(e.target.value)} className="w-full rounded-lg border border-slate-600 bg-slate-900 text-slate-100 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500">
            <option value="app-only">Keep in this app only</option>
            <option value="cross-app">Share insights across DDD apps I use (recommended)</option>
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button type="submit" disabled={submitting} className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-60">
            {submitting ? 'Saving…' : 'I agree, start coaching'}
          </button>
          {onDecline && <button type="button" onClick={onDecline} className="px-4 py-3 text-slate-400 hover:text-white font-medium">Not right now</button>}
        </div>
      </form>
    </div>
  );
}
